import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { captureLocation } from '../utils/locationTracker';
import './EquipmentRegistration.css';

const CONDITION_OPTIONS = [
  { value: 'good', label: '✅ Good' },
  { value: 'fair', label: '⚠️ Fair' },
  { value: 'poor', label: '🔴 Poor' }
];

function EquipmentRegistration({ user, equipmentType, onBack }) {
  const [step, setStep] = useState(1); // 1: form, 2: success
  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [outletSearch, setOutletSearch] = useState('');
  const [showOutletDropdown, setShowOutletDropdown] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    equipmentType: equipmentType || 'keg_pump',
    serialNumber: '',
    outletId: '',
    outletName: '',
    installationDate: new Date().toISOString().split('T')[0],
    condition: 'good',
    notes: ''
  });

  const equipmentLabel = form.equipmentType === 'keg_pump' ? 'Keg Pump' : 'Draft Machine';
  const equipmentIcon = form.equipmentType === 'keg_pump' ? '☕' : '🍺';

  // Load outlets from Firestore
  useEffect(() => {
    const loadOutlets = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'outlets'), where('status', '==', 'active'))
        );
        const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOutlets(list);
      } catch (err) {
        console.error('Error loading outlets:', err);
      }
    };
    loadOutlets();
  }, []);

  // ✅ FIXED: using correct Firebase field names — outletName and area
  const filteredOutlets = outlets.filter(o => {
    const name = typeof o.outletName === 'string' ? o.outletName.toLowerCase() : '';
    const area = typeof o.area === 'string' ? o.area.toLowerCase() : '';
    const address = typeof o.address === 'string' ? o.address.toLowerCase() : '';
    const contact = typeof o.contactPerson === 'string' ? o.contactPerson.toLowerCase() : '';
    const search = outletSearch.toLowerCase();
    return (
      name.includes(search) ||
      area.includes(search) ||
      address.includes(search) ||
      contact.includes(search)
    );
  });

  const handleOutletSelect = (outlet) => {
    setSelectedOutlet(outlet);
    setOutletSearch(outlet.outletName); // ✅ correct field
    setForm(prev => ({
      ...prev,
      outletId: outlet.id,
      outletName: outlet.outletName // ✅ correct field
    }));
    setShowOutletDropdown(false);
    setErrors(prev => ({ ...prev, outlet: '' }));
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.serialNumber.trim()) newErrors.serialNumber = 'Serial number is required';
    if (!form.outletId) newErrors.outlet = 'Please select an outlet';
    if (!form.installationDate) newErrors.installationDate = 'Installation date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      // Check for duplicate serial number
      const dupQuery = query(
        collection(db, 'equipment'),
        where('serialNumber', '==', form.serialNumber.trim().toUpperCase())
      );
      const dupSnap = await getDocs(dupQuery);
      if (!dupSnap.empty) {
        setErrors({ serialNumber: 'This serial number is already registered' });
        setLoading(false);
        return;
      }

      const location = await captureLocation(user, 'EquipmentRegistration');

      await addDoc(collection(db, 'equipment'), {
        equipmentType: form.equipmentType,
        serialNumber: form.serialNumber.trim().toUpperCase(),
        outletId: form.outletId,
        outletName: form.outletName,
        area: selectedOutlet?.area || '',
        address: selectedOutlet?.address || '',
        contactPerson: selectedOutlet?.contactPerson || '',
        contactPhone: selectedOutlet?.contactPhone || '',
        installationDate: form.installationDate,
        condition: form.condition,
        notes: form.notes.trim(),
        status: 'active',
        technicianId: user.id,
        technicianName: user.name,
        location: location || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setStep(2);
    } catch (err) {
      console.error('Error registering equipment:', err);
      alert('Failed to register equipment. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAnother = () => {
    setForm({
      equipmentType: form.equipmentType,
      serialNumber: '',
      outletId: '',
      outletName: '',
      installationDate: new Date().toISOString().split('T')[0],
      condition: 'good',
      notes: ''
    });
    setSelectedOutlet(null);
    setOutletSearch('');
    setErrors({});
    setStep(1);
  };

  // ── Success Screen ────────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="eqr-page">
        <div className="eqr-success-card">
          <div className="eqr-success-icon">✅</div>
          <h2 className="eqr-success-title">Equipment Registered!</h2>
          <p className="eqr-success-text">
            {equipmentIcon} <strong>{form.serialNumber.toUpperCase()}</strong> has been
            registered at <strong>{form.outletName}</strong>
          </p>
          <div className="eqr-success-actions">
            <button className="eqr-primary-btn" onClick={handleRegisterAnother}>
              + Register Another
            </button>
            <button className="eqr-secondary-btn" onClick={onBack}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration Form ─────────────────────────────────────────────────────
  return (
    <div className="eqr-page">
      {/* Header */}
      <header className="eqr-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="eqr-header-title">
          <span className="eqr-header-icon">{equipmentIcon}</span>
          <div>
            <h1 className="eqr-header-h1">Equipment Registration</h1>
            <p className="eqr-header-sub">Register a new {equipmentLabel}</p>
          </div>
        </div>
      </header>

      <div className="eqr-form-wrapper">

        {/* Equipment Type Selector */}
        <div className="eqr-section">
          <label className="eqr-label">Equipment Type</label>
          <div className="eqr-toggle-row">
            <button
              className={`eqr-toggle-btn ${form.equipmentType === 'keg_pump' ? 'active' : ''}`}
              onClick={() => handleChange('equipmentType', 'keg_pump')}
            >
              ☕ Keg Pump
            </button>
            <button
              className={`eqr-toggle-btn ${form.equipmentType === 'draft_machine' ? 'active' : ''}`}
              onClick={() => handleChange('equipmentType', 'draft_machine')}
            >
              🍺 Draft Machine
            </button>
          </div>
        </div>

        {/* Serial Number */}
        <div className="eqr-section">
          <label className="eqr-label">Serial Number *</label>
          <input
            className={`eqr-input ${errors.serialNumber ? 'error' : ''}`}
            placeholder="e.g. KP-2024-001"
            value={form.serialNumber}
            onChange={e => handleChange('serialNumber', e.target.value)}
          />
          {errors.serialNumber && <p className="eqr-error-text">⚠️ {errors.serialNumber}</p>}
        </div>

        {/* Outlet Search */}
        <div className="eqr-section">
          <label className="eqr-label">Outlet / Location *</label>
          <div className="eqr-dropdown-wrapper">
            <input
              className={`eqr-input ${errors.outlet ? 'error' : ''}`}
              placeholder="Search by outlet name, area or contact..."
              value={outletSearch}
              onChange={e => {
                setOutletSearch(e.target.value);
                setShowOutletDropdown(true);
                if (!e.target.value) {
                  setSelectedOutlet(null);
                  setForm(prev => ({ ...prev, outletId: '', outletName: '' }));
                }
              }}
              onFocus={() => setShowOutletDropdown(true)}
            />
            {showOutletDropdown && outletSearch.length > 0 && (
              <div className="eqr-dropdown">
                {filteredOutlets.length === 0 ? (
                  <div className="eqr-dropdown-empty">No outlets found</div>
                ) : (
                  filteredOutlets.slice(0, 8).map(outlet => (
                    <div
                      key={outlet.id}
                      className="eqr-dropdown-item"
                      onClick={() => handleOutletSelect(outlet)}
                    >
                      <span className="eqr-dropdown-name">🏪 {outlet.outletName}</span>
                      <span className="eqr-dropdown-sub">
                        {[outlet.area, outlet.address].filter(Boolean).join(' · ')}
                      </span>
                      {outlet.contactPerson && (
                        <span className="eqr-dropdown-contact">👤 {outlet.contactPerson}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {errors.outlet && <p className="eqr-error-text">⚠️ {errors.outlet}</p>}
          {selectedOutlet && (
            <div className="eqr-selected-badge">
              ✅ {selectedOutlet.outletName}
              {selectedOutlet.area ? ` — ${selectedOutlet.area}` : ''}
            </div>
          )}
        </div>

        {/* Installation Date */}
        <div className="eqr-section">
          <label className="eqr-label">Installation Date *</label>
          <div className={`eqr-input eqr-date-shell ${errors.installationDate ? 'error' : ''}`}>
            <span className="eqr-date-display">
              {form.installationDate
                ? (() => {
                    const [yyyy, mm, dd] = form.installationDate.split('-');
                    return `${mm} / ${dd} / ${yyyy}`;
                  })()
                : 'Select date'}
            </span>
            <input
              type="date"
              value={form.installationDate}
              onChange={e => handleChange('installationDate', e.target.value)}
              className="eqr-date-hidden"
            />
          </div>
          {errors.installationDate && (
            <p className="eqr-error-text">⚠️ {errors.installationDate}</p>
          )}
        </div>

        {/* Technician (auto-filled) */}
        <div className="eqr-section">
          <label className="eqr-label">Installed By</label>
          <div className="eqr-readonly-field">👤 {user.name}</div>
        </div>

        {/* Condition */}
        <div className="eqr-section">
          <label className="eqr-label">Condition</label>
          <div className="eqr-condition-row">
            {CONDITION_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`eqr-condition-btn cond-${opt.value} ${form.condition === opt.value ? 'active' : ''}`}
                onClick={() => handleChange('condition', opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="eqr-section">
          <label className="eqr-label">Notes (optional)</label>
          <textarea
            className="eqr-textarea"
            placeholder="Any additional observations..."
            value={form.notes}
            onChange={e => handleChange('notes', e.target.value)}
            rows={3}
          />
        </div>

        {/* Submit */}
        <button
          className={`eqr-submit-btn ${loading ? 'loading' : ''}`}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? '⏳ Registering...' : `${equipmentIcon} Register ${equipmentLabel}`}
        </button>

      </div>
    </div>
  );
}

export default EquipmentRegistration;
